<template>
    <!-- Диалог RD-148 поверх подэкрана ТУ, по центру панели (не телепорт в body). -->
    <div class="ds-dossier-overlay" @keydown.escape="close">
        <div class="ds-glass-scrim ds-dossier-overlay-scrim" @click="close"></div>
        <div class="ds-glass-dialog ds-dossier-fire" role="dialog" aria-modal="true" aria-labelledby="dossier-fire-title">
            <div class="ds-dossier-fire-head">
                <span class="ds-dossier-fire-heading">
                    <span id="dossier-fire-title" class="ds-dossier-fire-title">Уволить сотрудника?</span>
                    <span class="ds-dossier-section-title">{{ employment.departmentName || DASH }}</span>
                    <span class="ds-dossier-muted">{{ subtitle }}</span>
                </span>
                <button type="button" class="ds-dossier-icon-button" aria-label="Закрыть" :disabled="pending" @click="close"><CloseIcon /></button>
            </div>

            <div class="ds-dossier-field ds-dossier-fire-field">
                <span class="ds-dossier-field-label">Последний рабочий день</span>
                <!-- AppCalendar RD-160 в режиме «Дата»; даты раньше начала ТУ недоступны, будущие допустимы. -->
                <div class="ds-glass-date ds-dossier-fire-date">
                    <AppDateInput v-model="dateModel" :min-date="minDate" :initial-date="initialMonth" />
                    <span class="ds-glass-date-icon"><CalendarIcon /></span>
                </div>
                <span v-if="dateError" class="ds-dossier-field-error">{{ dateError }}</span>
            </div>

            <span class="ds-dossier-note">Смены после этой даты пропадут из графика. Другие трудоустройства не меняются.</span>
            <div v-if="error" class="ds-dossier-field-error" role="alert">{{ error }}</div>

            <div class="ds-glass-dialog-actions">
                <button class="ds-glass-btn ds-glass-btn-secondary" type="button" :disabled="pending" @click="close">Отмена</button>
                <button class="ds-glass-btn ds-dossier-btn-danger" type="button" :disabled="!canConfirm" @click="confirm">{{ pending ? 'Увольнение…' : 'Уволить' }}</button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import AppDateInput from '@/ui/AppDateInput.vue'
import { editEmployment } from '@/api/users'
import { formatEmploymentDateForApi } from '@/utils/employmentDate'
import { type DossierEmployment, type DossierPerson, DASH, describeWriteError, formatDate, shortName } from './dossierModel'
import { CalendarIcon, CloseIcon } from './dossierIcons'

// Увольнение: PATCH /api/employments/{id}/ только {fired_at}; не DELETE, другие ТУ и смены не трогаются
// (смены после даты скрывает текущий backend — подтверждено Романом 23.10.2026).
const props = defineProps<{
    person: DossierPerson
    employment: DossierEmployment
    today: Dayjs
}>()
const emit = defineEmits<{ close: [], fired: [] }>()

const minDate = computed(() => (props.employment.hiredAt ? dayjs(props.employment.hiredAt.slice(0, 10)) : undefined))
// Default — сегодня; если ТУ начинается в будущем, сегодня невалидно до выбора допустимой даты.
const date = ref<Dayjs | null>(props.today)
const dateModel = computed({
    get: () => date.value,
    set: (value: unknown) => { date.value = (value as Dayjs | null) ?? null },
})
const initialMonth = computed(() => (minDate.value && minDate.value.isAfter(props.today, 'day') ? minDate.value : props.today))
const dateError = computed(() => {
    if (!date.value || !dayjs(date.value).isValid()) return 'Выберите дату'
    if (minDate.value && dayjs(date.value).isBefore(minDate.value, 'day')) return `Не раньше даты начала ${minDate.value.format('DD.MM.YYYY')}`
    return ''
})

const subtitle = computed(() => [
    shortName(props.person) || null,
    props.employment.positionName,
    props.employment.hiredAt ? `с ${formatDate(props.employment.hiredAt)}` : null,
].filter(Boolean).join(' · '))

const pending = ref(false)
const error = ref('')
const canConfirm = computed(() => !pending.value && !dateError.value)

const close = () => {
    if (!pending.value) emit('close')
}

async function confirm() {
    const firedAt = formatEmploymentDateForApi(date.value)
    if (!canConfirm.value || !firedAt) return
    pending.value = true
    error.value = ''
    try {
        await editEmployment(props.employment.id, { fired_at: firedAt }, { suppressGlobalErrorToast: true })
    } catch (caught) {
        // Ошибка остаётся в диалоге; автоповтора нет.
        error.value = describeWriteError(caught, 'Не удалось уволить')
        pending.value = false
        return
    }
    pending.value = false
    emit('fired')
}
</script>
